import React, { useState } from 'react';

import { Helmet } from 'react-helmet';
import { useParams } from 'react-router-dom';

import { Alert, Box, Container, Paper, Tab, Tabs } from '@mui/material';

import { useGetProperty } from '~/hooks/apis/accommodation/useGetProperty';
import { useGetOffer } from '~/hooks/apis/offers/useGetOffer';

import OfferBuilderImagesVideo from './OfferBuilderImagesVideo';
import OfferBuilderPropertyInformation, { formatPropertyLocation } from './OfferBuilderPropertyInformation';
import type { OfferBuilderProperty } from './OfferBuilderPropertyInformation';
import OfferBuilderSidebar from './OfferBuilderSidebar';

const DEFAULT_TAB = 'Property information';
const IMAGES_VIDEO_TAB = 'Images & Video';

const CONTENT_TABS = [DEFAULT_TAB, 'Rooms', 'Offer packages', 'Default content', 'Generated content', IMAGES_VIDEO_TAB];

export default function OfferBuilderContentPage() {
  const { id_offer: offerId } = useParams<{ id_offer: string }>();
  const [activeTab, setActiveTab] = useState(DEFAULT_TAB);

  const { data: offer, isLoading: isLoadingOffer, error: offerError } = useGetOffer(offerId);
  const propertyId = offer?.packages?.find((offerPackage) => offerPackage?.fk_property_id)?.fk_property_id;
  const vendorId = offer?.vendor_account_id;

  const {
    data: property,
    isLoading: isLoadingProperty,
    error: propertyError,
  } = useGetProperty<OfferBuilderProperty>({
    propertyId,
  });

  const hotelName = property?.name || propertyId || 'Property';
  const propertyLocation = formatPropertyLocation(property);

  return (
    <Container maxWidth="xl">
      <Helmet>
        <title>Offer Builder | {hotelName}</title>
      </Helmet>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '280px minmax(0, 1fr)' } }}>
        <OfferBuilderSidebar hotelCode={property?.hotel_code} hotelName={hotelName} location={propertyLocation} />

        <Box sx={{ px: { xs: 2, md: 4 }, py: 3, pb: 14 }}>
          <Paper sx={{ position: 'sticky', top: 0, zIndex: 2, mb: 3 }}>
            <Tabs
              value={activeTab}
              onChange={(_event, value) => setActiveTab(value)}
              variant="scrollable"
              scrollButtons="auto"
            >
              {CONTENT_TABS.map((tab) => (
                <Tab key={tab} value={tab} label={tab} />
              ))}
            </Tabs>
          </Paper>

          {activeTab === DEFAULT_TAB && (
            <OfferBuilderPropertyInformation
              property={property}
              vendorId={vendorId}
              isLoadingProperty={isLoadingOffer || isLoadingProperty}
              offerError={offerError}
              propertyError={propertyError}
            />
          )}
          {activeTab === IMAGES_VIDEO_TAB && (
            <OfferBuilderImagesVideo
              images={offer?.images}
              propertyId={property?.id}
              propertyLogoId={property?.logo_id_cloudinary_external}
              propertyImages={property?.images}
              propertyRoomTypes={property?.room_types}
              vendorId={vendorId}
              videos={offer?.videos}
            />
          )}
          {activeTab !== DEFAULT_TAB && activeTab !== IMAGES_VIDEO_TAB && <ComingSoon name={activeTab} />}
        </Box>
      </Box>
    </Container>
  );
}

function ComingSoon({ name }: { name: string }) {
  return (
    <Alert severity="info">
      <strong>{name}</strong> is coming soon.
    </Alert>
  );
}
